import { FC, useEffect, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";

import { ActivityLogType, InsightDay } from "../../DTO";
import { useAppSelector } from "../../redux-config/hooks";
import { selectWeek } from "../../redux-config/entities/week";
import { selectUser } from "../../redux-config/entities/user";
import { AppUserApi } from "../../api";
import {
    formatDuration,
    formatPatientDateTime,
} from "../../utils/health-report-format";
import { ReportActivityChart } from "../chart";
import { ActivityTargetTrace } from "../activity-target-trace";

export const ActivityReport: FC<{ userId: number; days?: InsightDay[] }> = ({
    userId,
    days,
}) => {
    const [loader, setLoader] = useState(false);
    const [activity, setActivity] = useState<ActivityLogType[]>([]);
    const week = useAppSelector(selectWeek);
    const user = useAppSelector(selectUser);

    useEffect(() => {
        setLoader(true);
        AppUserApi.ActivityLog(user, Number(userId), week.index)
            .then((data) => setActivity(data.activity))
            .catch((error) => Swal.fire({ icon: "error", text: error }))
            .finally(() => setLoader(false));
    }, [user, userId, week.index]);

    if (loader)
        return (
            <div className="health-report-loading">
                <Spinner animation="border" size="sm" /> Loading activity…
            </div>
        );

    return (
        <div className="health-report-stack">
            <ActivityTargetTrace
                userId={userId}
                selectedWeekIndex={week.index}
            />
            <section className="health-report-panel">
                <header className="health-report-heading">
                    <div>
                        <span>Recorded activity</span>
                        <h2>Daily activity log</h2>
                    </div>
                    <p>
                        Times are shown in the patient’s local time. Durations
                        are rounded to readable units.
                    </p>
                </header>
                {activity.length ? (
                    <Table
                        responsive
                        className="health-report-table align-middle"
                    >
                        <thead>
                            <tr>
                                <th>Day</th>
                                <th>Steps</th>
                                <th>Active time</th>
                                <th>Recorded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activity.map((item) => {
                                const recorded = formatPatientDateTime(
                                    item.createdAt,
                                    item.timezoneOffset,
                                );
                                return (
                                    <tr key={item.id}>
                                        <td>{recorded.date}</td>
                                        <td>
                                            <strong>
                                                {Number(
                                                    item.totalSteps || 0,
                                                ).toLocaleString()}
                                            </strong>{" "}
                                            steps
                                        </td>
                                        <td>
                                            {formatDuration(
                                                item.totalDurationTime,
                                            )}
                                        </td>
                                        <td>
                                            <strong>{recorded.time}</strong>
                                            <small>Patient local time</small>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : (
                    <div className="health-report-empty">
                        <strong>No activity recorded</strong>
                        <span>
                            This week has no step or active-time entries yet.
                        </span>
                    </div>
                )}
            </section>
            <ReportActivityChart activity={activity} days={days} />
        </div>
    );
};
